import { memo, useMemo } from "react";
import { ToolUIPart } from "ai";

import {
  ActionIcon,
  Badge,
  Collapse,
  Group,
  List,
  Loader,
  Mark,
  Paper,
  Text,
  ThemeIcon
} from "@mantine/core";
import {
  TbAlertTriangle,
  TbChevronDown,
  TbChevronUp,
  TbCircleCheck,
  TbClock,
  TbForbid,
  TbStack,
  TbStack2,
  TbTool
} from "react-icons/tb";

import { useDisclosure } from "@mantine/hooks";

import { ChatToolType } from "modules/chat/types/entities";
import { IRollupProject } from "modules/projects/types/entities";

type ToolMessageProps = {
  part: ToolUIPart;
  projects: IRollupProject[];
};

type ToolMessageInput = {
  query?: string;
  projectIds?: string[];
};

const toolNameMap: { [key in ChatToolType]?: string } = {
  "tool-experiments_semantic_search": "Searching experiments",
  "tool-insights_semantic_search": "Searching insights",
  "tool-observations_semantic_search": "Searching observations",
  "tool-objectives_semantic_search": "Searching objectives",
  "tool-ideas_semantic_search": "Searching ideas"
};

const ToolMessage = (props: ToolMessageProps) => {
  const { part, projects } = props;

  const [isDetailsOpen, { toggle }] = useDisclosure(false);

  const toolType = part.type as ChatToolType;
  const input = part.input as ToolMessageInput | undefined;

  const stateLabel = useMemo(() => {
    if (part.state === "approval-requested") return "Awaiting approval";
    if (part.state === "approval-responded") return "Approved";
    if (part.state === "output-denied") return "Denied";
    if (part.state === "output-error") return "Error";
    if (part.state === "output-available") return "Completed";

    return "Pending";
  }, [part.state]);

  const icon = useMemo(() => {
    if (part.state === "output-available") return <TbCircleCheck />;
    if (part.state === "approval-requested") return <TbClock />;
    if (part.state === "approval-responded") return <TbCircleCheck />;
    if (part.state === "output-denied") return <TbForbid />;
    if (part.state === "output-error") return <TbAlertTriangle />;
    return <Loader size={10} />;
  }, [part.state]);

  const color = useMemo(() => {
    if (part.state === "output-available") return "green";
    if (part.state === "approval-requested") return "blue";
    if (part.state === "approval-responded") return "green";
    if (part.state === "output-denied") return "red";
    if (part.state === "output-error") return "red";
    return "gray";
  }, [part.state]);

  return (
    <>
      <Group gap="xs">
        <ThemeIcon>
          <TbTool />
        </ThemeIcon>

        <Text size="sm">{toolNameMap[toolType]}</Text>

        <Badge
          size="sm"
          leftSection={icon}
          color="gray"
          c="dark"
          tt="capitalize"
          variant="outline"
          styles={{
            section: {
              color: color,
              fontSize: 12
            }
          }}
        >
          {stateLabel}
        </Badge>

        {input?.projectIds || input?.query ? (
          <ActionIcon onClick={toggle} size="xs">
            {isDetailsOpen ? <TbChevronUp /> : <TbChevronDown />}
          </ActionIcon>
        ) : null}
      </Group>

      <Collapse in={isDetailsOpen}>
        {input?.projectIds ? (
          <Paper bg="gray.0" py="xs" px="sm" mt="xs">
            {input.projectIds.length ? (
              <div>
                <Text size="xs" c="gray.7" mb="xs">
                  Searching{" "}
                  {input.query ? (
                    <span>
                      for{" "}
                      <Mark mr={2} px={2}>
                        {input.query}
                      </Mark>
                    </span>
                  ) : (
                    ""
                  )}
                  in projects:
                </Text>

                <List icon={<TbStack />}>
                  {input.projectIds.map(projectId => {
                    const project = projects.find(
                      project => project.id === projectId
                    );
                    return project ? (
                      <List.Item key={projectId}>
                        <Text size="xs">{project.name}</Text>
                      </List.Item>
                    ) : null;
                  })}
                </List>
              </div>
            ) : (
              <Group>
                <TbStack2 style={{ flexShrink: 0 }} />

                <Text size="xs" c="gray.7">
                  Searching{" "}
                  {input.query ? (
                    <span>
                      for{" "}
                      <Mark mr={2} px={2}>
                        {input.query}
                      </Mark>
                    </span>
                  ) : (
                    ""
                  )}
                  in <strong>all projects</strong>
                </Text>
              </Group>
            )}
          </Paper>
        ) : null}
      </Collapse>
    </>
  );
};

export default memo(ToolMessage);
